import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import DataTable from '../../../sharedModules/dataTable';
import axios from '../../../sharedModules/utils/axios.utils';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

const docuwareInvoiceColumns = [
  {
    name: 'Location',
    selector: (row: any) => row.location,
    sortable: true,
    width: '120px',
  },
  {
    name: 'Vendor ID',
    selector: (row: any) => row.vendor_id,
    sortable: true,
    width: '120px',
  },
  {
    name: 'Invoice Number',
    selector: (row: any) => row.invoice_number,
    sortable: true,
    width: '150px',
  },
  {
    name: 'Invoice Date',
    selector: (row: any) => (row.invoice_date ? new Date(row.invoice_date).toLocaleDateString() : 'N/A'),
    sortable: true,
    width: '130px',
  },
  {
    name: 'Amount (Raw)',
    selector: (row: any) => row.invoice_amount_raw,
    sortable: true,
    cell: (row: any) => row.invoice_amount_raw || 'N/A',
    width: '130px',
  },
  {
    name: 'DocID',
    selector: (row: any) => row.docid || 'N/A',
    sortable: true,
    width: '150px',
  },
{
    name: 'Synced At',
    selector: (row: any) => (row.synced_at ? new Date(row.synced_at).toLocaleString() : 'N/A'),
    sortable: true,
    width: '180px',
  },
];

const DocuwareInvoicesListPage: React.FC = () => {
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInvoiceNumber, setSearchInvoiceNumber] = useState<string>('');
  const [selectedVendor, setSelectedVendor] = useState<string>('');
  const [filteredInvoices, setFilteredInvoices] = useState<any[]>([]);

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get<string, any>('/vendors/servman_docuware_invoices');
        setInvoices(response?.data || []);
        setFilteredInvoices(response?.data || []);
      } catch (error) {
        console.error('Error fetching DocuWare invoices:', error);
        setError('Failed to load DocuWare invoices. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, []);

  const uniqueVendors = useMemo(() => {
    const vendors = Array.from(new Set(invoices.map((inv) => inv.vendor_id).filter(Boolean)));
    return vendors.sort();
  }, [invoices]);

  const handleSearch = () => {
    let filtered = invoices;

    if (searchInvoiceNumber.trim()) {
      filtered = filtered.filter((invoice) =>
        invoice.invoice_number?.toLowerCase().includes(searchInvoiceNumber.toLowerCase()),
      );
    }

    if (selectedVendor) {
      filtered = filtered.filter((invoice) => invoice.vendor_id === selectedVendor);
    }

    setFilteredInvoices(filtered);
  };

  const handleClearSearch = () => {
    setSearchInvoiceNumber('');
    setSelectedVendor('');
    setFilteredInvoices(invoices);
  };

  useEffect(() => {
    handleSearch();
  }, [selectedVendor]);

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <Grid container>
          <Grid item xs={12} style={{ marginTop: 20 }}>
            <Typography style={{ fontSize: '20px' }} variant="h3">
              Vendor Management <NavigateNextIcon fontSize="small" style={{ margin: '-4px 4px' }} /> DocuWare Invoices
            </Typography>
          </Grid>
          <Grid item xs={12} style={{ marginTop: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>Loading invoices...</div>
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: '100%' }}>
        <Grid container>
          <Grid item xs={12} style={{ marginTop: 20 }}>
            <Typography style={{ fontSize: '20px' }} variant="h3">
              Vendor Management <NavigateNextIcon fontSize="small" style={{ margin: '-4px 4px' }} /> DocuWare Invoices
            </Typography>
          </Grid>
          <Grid item xs={12} style={{ marginTop: 20 }}>
            <div style={{ color: 'red', textAlign: 'center', padding: '50px' }}>{error}</div>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Grid container>
      <Grid item xs={12} style={{ marginTop: 20 }}>
        <Grid container justifyContent="space-between">
          <Grid item style={{ marginBottom: 3 }}>
            <Typography style={{ display: 'inline-block', fontSize: '20px' }} variant="h3">
              Vendor Management
            </Typography>
            <NavigateNextIcon
              fontSize="small"
              style={{
                color: '#3d5a80 !important',
                fontWeight: 700,
                fontSize: '20px !important',
                margin: '-4px 4px',
              }}
            />
            <Typography style={{ display: 'inline-block', fontSize: '20px' }} variant="h3">
              DocuWare Invoices
            </Typography>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12} style={{ marginTop: 20 }}>
        <Card>
          <CardContent>
            <Grid container spacing={2} alignItems="center" style={{ marginBottom: 20 }}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Search by Invoice Number"
                  value={searchInvoiceNumber}
                  onChange={(e) => setSearchInvoiceNumber(e.target.value)}
                  onKeyPress={handleKeyPress}
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel>Vendor ID</InputLabel>
                  <Select value={selectedVendor} onChange={(e) => setSelectedVendor(e.target.value)} label="Vendor ID">
                    <MenuItem value="">
                      <em>All Vendors</em>
                    </MenuItem>
                    {uniqueVendors.map((vendor) => (
                      <MenuItem key={vendor} value={vendor}>
                        {vendor}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item>
                <Button variant="contained" color="primary" startIcon={<SearchIcon />} onClick={handleSearch}>
                  Search
                </Button>
              </Grid>
              <Grid item>
                <Button variant="outlined" startIcon={<ClearIcon />} onClick={handleClearSearch}>
                  Clear
                </Button>
              </Grid>
            </Grid>
            <Box sx={{ mt: 3 }}>
              <DataTable
                title=""
                columns={docuwareInvoiceColumns}
                data={filteredInvoices}
                pagination
                paginationPerPage={25}
                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                highlightOnHover
                pointerOnHover
                responsive
                dense
                noHeader
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default DocuwareInvoicesListPage;
